<template>
    <div>
        <AdminPageHeader
            title="Materiales de Impresión 3D"
            description="Costo por kilo, densidad y márgenes que alimentan el motor de costeo."
        />

        <div class="adm-grid-2">
            <div class="adm-stack">
                <AdmCard :title="selected ? `Editando material #${selected.id}` : 'Nuevo material'">
                    <MaterialForm
                        :value="selected || {}"
                        :editing="!!selected"
                        :categories="categories"
                        @submit="onSubmit"
                        @cancel="resetSelection"
                        @create-category="onCreateCategory"
                    />
                    <template v-if="selected" #footer>
                        <AdmButton variant="danger" :icon="Trash2" @click="onDelete(selected.id)">
                            Eliminar material
                        </AdmButton>
                    </template>
                </AdmCard>
            </div>

            <div class="adm-stack">
                <AdmCard title="Materiales registrados">
                    <template #actions>
                        <label class="materiales__toggle">
                            <input v-model="filter.includeInactive" type="checkbox" />
                            Mostrar inactivos
                        </label>
                    </template>

                    <AdmField
                        v-model="filter.name"
                        label="Filtrar por nombre"
                        placeholder="PLA, PETG…"
                    />

                    <div class="materiales__list">
                        <article
                            v-for="m in items"
                            :key="m.id"
                            class="materiales__card"
                            :class="{ 'is-inactive': !m.isActive, 'is-selected': selected?.id === m.id }"
                        >
                            <header class="materiales__card-head">
                                <strong>#{{ m.id }} — {{ m.name }}</strong>
                                <AdmBadge v-if="!m.isActive" tone="neutral" label="Inactivo" />
                            </header>

                            <p v-if="m.manufacturer" class="adm-muted materiales__maker">{{ m.manufacturer }}</p>

                            <dl class="materiales__specs">
                                <div>
                                    <dt>Costo/kg</dt>
                                    <dd>{{ formatCurrency(m.costPerKg) }}</dd>
                                </div>
                                <div>
                                    <dt>Densidad</dt>
                                    <dd>{{ formatNumber(m.densityGcm3, 3) }} g/cm³</dd>
                                </div>
                                <div>
                                    <dt>Infill</dt>
                                    <dd>{{ formatNumber(m.printDensityPercent ?? 100, 3) }} %</dd>
                                </div>
                                <div>
                                    <dt>Ganancia est.</dt>
                                    <dd>{{ formatNumber(m.profitPercent, 3) }} %</dd>
                                </div>
                                <div v-if="m.maxVolumeMm3" class="materiales__specs-wide">
                                    <dt>Ganancia volumen grande</dt>
                                    <dd>
                                        {{ formatNumber(m.surchargePercent, 3) }} %
                                        si vol ≥ {{ formatNumber(m.maxVolumeMm3 / 1000, 3) }} cm³
                                    </dd>
                                </div>
                            </dl>

                            <footer class="materiales__card-foot">
                                <AdmButton variant="secondary" size="sm" :icon="Pencil" @click="select(m)">
                                    Editar
                                </AdmButton>
                            </footer>
                        </article>

                        <AdmEmptyState
                            v-if="!items.length"
                            message="No hay materiales que coincidan con el filtro."
                        />
                    </div>

                    <template #footer>
                        <AdmPagination :page="page" :total-pages="totalPages" @update:page="goToPage" />
                    </template>
                </AdmCard>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * Materiales de Impresión 3D — migrada en el plan 27_09_2026 (F3/T19).
 *
 * Se retiran la barra `#menu` duplicada, la redefinición local de `.button`
 * y el contenedor `.page` con su `max-width` propio. Las llamadas a
 * `dbPrint3dAdmin.js` y el cálculo de costeo no se tocan.
 */
import { Trash2, Pencil } from 'lucide-vue-next';
import MaterialForm from './MaterialForm.vue';
import {
    getMaterials, saveMaterial, editMaterial, deleteMaterial,
    getCategories, saveCategory,
} from '@/services/dbPrint3dAdmin.js';
import { formatNumberRAE, formatCurrencyRAE } from '@/utils/numberFormat';
import AdminPageHeader from '@/components/Admin/AdminPageHeader.vue';
import AdmCard from '@/components/Admin/ui/AdmCard.vue';
import AdmField from '@/components/Admin/ui/AdmField.vue';
import AdmButton from '@/components/Admin/ui/AdmButton.vue';
import AdmBadge from '@/components/Admin/ui/AdmBadge.vue';
import AdmPagination from '@/components/Admin/ui/AdmPagination.vue';
import AdmEmptyState from '@/components/Admin/ui/AdmEmptyState.vue';
import { notify } from '@/composables/useAdmToast.js';

export default {
    name: 'Print3DMaterialesPage',
    components: {
        MaterialForm, AdminPageHeader, AdmCard, AdmField,
        AdmButton, AdmBadge, AdmPagination, AdmEmptyState,
    },
    data() {
        return {
            items: [],
            categories: [],
            selected: null,
            page: 1,
            limit: 8,
            totalPages: 1,
            filter: { name: '', includeInactive: false },
            debounceTimer: null,
            Trash2, Pencil,
        };
    },
    watch: {
        filter: {
            deep: true,
            handler() {
                clearTimeout(this.debounceTimer);
                this.debounceTimer = setTimeout(() => {
                    this.page = 1;
                    this.fetchItems();
                }, 350);
            },
        },
    },
    methods: {
        async fetchItems() {
            const offset = (this.page - 1) * this.limit;
            const { items, totalPages } = await getMaterials(offset, this.limit, this.filter);
            this.items = items;
            this.totalPages = totalPages;
        },
        async fetchCategories() {
            const { categories } = await getCategories();
            this.categories = categories;
        },
        async onCreateCategory(data) {
            const { error } = await saveCategory(data);
            if (error) {
                notify.error(`Error al crear categoría: ${error}`);
                return;
            }
            notify.success('Categoría creada.');
            await this.fetchCategories();
        },
        select(m) { this.selected = { ...m }; },
        resetSelection() { this.selected = null; },
        goToPage(page) { this.page = page; this.fetchItems(); },
        async onSubmit(payload) {
            if (this.selected) {
                const { error } = await editMaterial(this.selected.id, payload);
                if (error) {
                    notify.error(`Error al editar: ${error}`);
                    return;
                }
                notify.success('Material actualizado.');
            } else {
                const { error } = await saveMaterial(payload);
                if (error) {
                    notify.error(`Error al crear: ${error}`);
                    return;
                }
                notify.success('Material creado.');
            }
            this.resetSelection();
            this.fetchItems();
        },
        async onDelete(id) {
            if (!confirm('¿Eliminar este material? (soft-delete; productos activos lo bloquean)')) return;
            const { error, details } = await deleteMaterial(id);
            if (error) {
                let msg = error;
                if (details && details.length) {
                    msg += ` · Productos enlazados: ${details.map(d => `#${d.id} ${d.name}`).join(', ')}`;
                }
                notify.error(msg, { duration: 0 });
                return;
            }
            notify.success('Material eliminado.');
            this.resetSelection();
            this.fetchItems();
        },
        formatNumber(value, digits = 2) {
            return formatNumberRAE(value, digits);
        },
        formatCurrency(value) {
            return formatCurrencyRAE(value);
        },
    },
    mounted() { this.fetchItems(); this.fetchCategories(); },
};
</script>

<style scoped>
.materiales__toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: var(--adm-font-sm);
    color: var(--color-text);
}

.materiales__list {
    display: flex;
    flex-direction: column;
    gap: var(--adm-gap-sm);
    margin-top: var(--adm-gap);
}

.materiales__card {
    padding: 0.8rem;
    border: 1px solid var(--adm-border);
    border-radius: var(--adm-radius-sm);
    background-color: var(--adm-surface);
}

.materiales__card.is-inactive { opacity: 0.65; }

.materiales__card.is-selected {
    border-color: var(--color-brand);
    background-color: var(--adm-info-bg);
}

.materiales__card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--adm-gap-sm);
}

.materiales__maker { margin: 0.15rem 0 0 0; }

.materiales__specs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    gap: 0.4rem var(--adm-gap);
    margin: 0.6rem 0 0 0;
}

.materiales__specs-wide { grid-column: 1 / -1; }

.materiales__specs dt {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--color-text);
}

.materiales__specs dd {
    margin: 0;
    font-size: var(--adm-font-sm);
    color: var(--color-text-strong);
}

.materiales__card-foot {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.6rem;
}
</style>
